import { DateRange } from '@epicuro-next/ataraxia/models';
import {
  datesEqual,
  intervalISODateToDateRange,
  isInDateRange,
  isIntervalISODate,
  startOfDayDate,
  unique,
  utcDateTime,
} from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import {
  BulkTypeDeliveryPeriod,
  BusinessState,
  DeliveryItem,
  DeliveryItems,
  DeliveryItemsByPeriod,
  DeliveryPeriodsByShop,
  DeliveryPeriodsByType,
  DeliveryPeriodType,
} from '../../../models';
import { DeliveryPeriodsCache } from '../../../models/delivery-periods-cache.model';
import { getDeliveryShopKey } from '../../../shared/shared';

const mergeFutureDatesWithDeliveryItemsDates = (
  deliveryItemsByShop: DeliveryItemsByPeriod,
  dates: string[],
): string[] => {
  const deliveryItemsPeriodsByShopForFuture = Object.entries(
    deliveryItemsByShop,
  )
    .filter(
      ([, deliveryItems]) =>
        deliveryItems.orderType === OrderType.Future &&
        Object.values(deliveryItems.items).every(isDeliveryItemForFuture),
    )
    .reduce(reduceDeliveryItemsToDeliveryDates, []);
  return [...dates, ...deliveryItemsPeriodsByShopForFuture].filter(unique);
};

const mergeBulkDatesWithDeliveryItemsDates = (
  deliveryItemsByShop: DeliveryItemsByPeriod,
  dates: string[],
): string[] => {
  const deliveryItemsPeriodsByShopForFuture = Object.entries(
    deliveryItemsByShop,
  )
    .filter(
      ([, deliveryItems]) =>
        deliveryItems.orderType === OrderType.Future &&
        Object.values(deliveryItems.items).every(isDeliveryItemForBulk),
    )
    .reduce(reduceDeliveryItemsToDeliveryDates, []);
  return [...dates, ...deliveryItemsPeriodsByShopForFuture].filter(unique);
};

const getFutureSelectedDate = (
  cacheSelectedDate: string | null,
  existingFutureDelivery: DeliveryPeriodsByType<string> | undefined,
  cachedDeliveryPeriods: string[],
): string | null => {
  const preselected =
    cacheSelectedDate && !isIntervalISODate(cacheSelectedDate)
      ? cacheSelectedDate
      : null;

  if (existingFutureDelivery && existingFutureDelivery.selectedDeliveryPeriod) {
    return existingFutureDelivery.selectedDeliveryPeriod;
  }
  return preselected || cachedDeliveryPeriods[0] || null;
};

const getBulkSelectedDate = (
  cacheSelectedDate: string | null,
  existingBulkDelivery:
    | DeliveryPeriodsByType<BulkTypeDeliveryPeriod>
    | undefined,
  cachedDeliveryPeriods: string[],
): BulkTypeDeliveryPeriod | null => {
  const preselected =
    cacheSelectedDate && isIntervalISODate(cacheSelectedDate)
      ? (intervalISODateToDateRange(
          cacheSelectedDate,
        ) as BulkTypeDeliveryPeriod)
      : null;

  if (existingBulkDelivery && existingBulkDelivery.selectedDeliveryPeriod) {
    return existingBulkDelivery.selectedDeliveryPeriod;
  }
  if (preselected) {
    return preselected;
  }
  if (cachedDeliveryPeriods.length) {
    return intervalISODateToDateRange(
      cachedDeliveryPeriods[0],
    ) as BulkTypeDeliveryPeriod;
  }
  return null;
};

const isFuturePeriodEnabled =
  (availableDateRange: DateRange) => (date: string) => {
    const utcDateBeginOfDay = startOfDayDate(
      utcDateTime(date).toJSDate(),
    ).toJSDate();
    const utcFromBeginOfDay = startOfDayDate(
      utcDateTime(availableDateRange.from).toJSDate(),
    ).toJSDate();
    const utcToBeginOfDay = startOfDayDate(
      utcDateTime(availableDateRange.to).toJSDate(),
    ).toJSDate();
    return (
      datesEqual(utcDateBeginOfDay, utcFromBeginOfDay) ||
      datesEqual(utcDateBeginOfDay, utcToBeginOfDay) ||
      isInDateRange(utcDateBeginOfDay, utcFromBeginOfDay, utcToBeginOfDay)
    );
  };

const isDeliveryItemForFuture = (item: DeliveryItem) => {
  return !item.consignmentStartDate && !item.consignmentEndDate;
};

const isDeliveryItemForBulk = (item: DeliveryItem) => {
  return item.consignmentStartDate && item.consignmentEndDate;
};

const reduceDeliveryItemsToDeliveryDates: (
  prevValue: string[],
  currentValue: [string, DeliveryItems],
) => string[] = (dates, [newDate]) => [...dates, newDate];

/** copies delivery periods to the store
 * works on reload by copying items from cache to each and every
 * store (taken into account range) DeliveryPeriodsEffects.copyFutureDeliveryPeriodsFromStore$
 */
export function copyDeliveryPeriodsFromCacheBusiness(
  state: BusinessState,
  { cache, availableDateRange, deliveryShops }: DeliveryPeriodsCache,
): BusinessState {
  const futureDates = cache.selectedDeliveryPeriods.filter(
    (date) => !isIntervalISODate(date),
  );
  const enabledFutureDates = futureDates.filter(
    isFuturePeriodEnabled(availableDateRange),
  );
  const disabledFutureDates = futureDates.filter(
    (date) => !isFuturePeriodEnabled(availableDateRange)(date),
  );
  const bulkDates = cache.selectedDeliveryPeriods.filter(isIntervalISODate);

  const deliveryShopsDeliveryPeriods = deliveryShops
    ?.map((deliveryShop) => {
      const deliveryShopId = getDeliveryShopKey(deliveryShop);
      const deliveryPeriodsByShop = state.deliveryPeriods[deliveryShopId] ?? {};

      const deliveryItemsByShop: DeliveryItemsByPeriod =
        state.deliveryItems[deliveryShopId] ?? {};

      const resultFutureDeliveryPeriods =
        mergeFutureDatesWithDeliveryItemsDates(
          deliveryItemsByShop,
          enabledFutureDates,
        );
      const resultBulkDeliveryPeriods = mergeBulkDatesWithDeliveryItemsDates(
        deliveryItemsByShop,
        bulkDates,
      );

      return {
        [deliveryShopId]: {
          ...deliveryPeriodsByShop,
          [DeliveryPeriodType.Future]: {
            selectedDeliveryPeriod: getFutureSelectedDate(
              cache.lastSelectedDate,
              deliveryPeriodsByShop[DeliveryPeriodType.Future],
              resultFutureDeliveryPeriods,
            ),
            unavailableDeliveryPeriods: disabledFutureDates,
            deliveryPeriods: resultFutureDeliveryPeriods,
          },
          [DeliveryPeriodType.Bulk]: {
            selectedDeliveryPeriod: getBulkSelectedDate(
              cache.lastSelectedDate,
              deliveryPeriodsByShop[DeliveryPeriodType.Bulk],
              resultBulkDeliveryPeriods,
            ),
            deliveryPeriods: resultBulkDeliveryPeriods.map(
              intervalISODateToDateRange,
            ),
          },
        } as unknown as DeliveryPeriodsByShop,
      };
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return {
    ...state,
    deliveryPeriods: {
      ...deliveryShopsDeliveryPeriods,
    },
  };
}
